/** Minimal semver: parse, compare, ranges (`^`, `~`, `>=a <b`, exact, `*`). */
export interface SemVer {
  major: number;
  minor: number;
  patch: number;
}

const RE = /^(\d+)\.(\d+)\.(\d+)(?:[-+].*)?$/;

export function parseSemver(v: string): SemVer | null {
  const m = RE.exec(v.trim());
  if (!m) return null;
  return { major: +m[1], minor: +m[2], patch: +m[3] };
}

export function isSemver(v: string): boolean {
  return parseSemver(v) !== null;
}

export function compareSemver(a: string | SemVer, b: string | SemVer): number {
  const x = typeof a === 'string' ? parseSemver(a) : a;
  const y = typeof b === 'string' ? parseSemver(b) : b;
  if (!x || !y) throw new Error('Invalid semver');
  return x.major - y.major || x.minor - y.minor || x.patch - y.patch;
}

function cmpOk(op: string, c: number): boolean {
  switch (op) {
    case '>=': return c >= 0;
    case '>': return c > 0;
    case '<=': return c <= 0;
    case '<': return c < 0;
    case '=': return c === 0;
    default: return false;
  }
}

export function satisfies(version: string, range: string): boolean {
  const v = parseSemver(version);
  if (!v) return false;
  const r = range.trim();
  if (r === '*' || r === '') return true;
  if (r.startsWith('^')) {
    const b = parseSemver(r.slice(1));
    if (!b) return false;
    if (compareSemver(v, b) < 0) return false;
    if (b.major > 0) return v.major === b.major;
    if (b.minor > 0) return v.major === 0 && v.minor === b.minor;
    return v.major === 0 && v.minor === 0 && v.patch === b.patch;
  }
  if (r.startsWith('~')) {
    const b = parseSemver(r.slice(1));
    if (!b) return false;
    return compareSemver(v, b) >= 0 && v.major === b.major && v.minor === b.minor;
  }
  const parts = r.split(/\s+/);
  return parts.every((p) => {
    const m = /^(>=|<=|>|<|=)?(.+)$/.exec(p);
    if (!m) return false;
    const b = parseSemver(m[2]);
    if (!b) return false;
    return cmpOk(m[1] ?? '=', compareSemver(v, b));
  });
}

export function isValidRange(range: string): boolean {
  const r = range.trim();
  if (r === '*') return true;
  if (r.startsWith('^') || r.startsWith('~')) return isSemver(r.slice(1));
  return r.split(/\s+/).every((p) => /^(>=|<=|>|<|=)?\d+\.\d+\.\d+$/.test(p));
}
